import React, { ChangeEvent, FC, MouseEvent, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  SelectChangeEvent,
  Slider,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Check } from '@mui/icons-material';
import SurveyContactData from '../../../../components/SurveyContactData/SurveyContactData';
import DesignChanger from '../../../../components/DesignChanger/DesignChanger';
import { ReactComponent as FlagDe } from '../../../../assets/images/flags/de.svg';
import { ReactComponent as FlagGb } from '../../../../assets/images/flags/gb.svg';
import { ReactComponent as FlagNl } from '../../../../assets/images/flags/nl.svg';
import { ISurvey } from '../../../../shared/interfaces/ISurvey';
import PlatformSelect from '../../../../components/PlatformSelect/PlatformSelect';
import { IPlatform } from '../../../../shared/interfaces/IPlatform';
import PlatformService from '../../../../services/PlatformService';
import { IDesign } from '../../../../shared/interfaces/IDesign';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../../providers/AuthProvider';
import { INpsEntry } from '../../../../shared/interfaces/INpsEntry';
import SurveyService from '../../../../services/SurveyService';
import { IContactData } from '../../../../shared/interfaces/IContactData';
import SurveyUtils from '../../../../shared/SurveyUtils';

const defaultDesign: IDesign = {
  companyLogo: '',
  primaryColor: '#357E8B',
  secondaryColor: '#000000',
  customFont: '',
  customFontFormat: '',
};

const defaultContactData = {
  privacyPolicyLink: '',
  contactEmail: '',
  imprintLink: '',
};

const defaultNpsData = {
  archived: false,
  archivedAt: '',
  averageRating: 0,
  companyName: '',
  companyWebsiteUrl: '',
  contactForm: false,
  createdAt: '',
  customFontFormat: '',
  id: '',
  label: '',
  lang: 'de',
  note: '',
  platform: '',
  question: 'Wie wahrscheinlich ist es, dass Sie uns weiterempfehlen?',
  questionEditable: false,
  score: 9,
  totalCount: 0,
  type: 'NPS',
  updatedAt: '',
  platformUrl: '',
  platformName: '',
  slug: '',
  title: 'Vielen Dank für Ihre Teilnahme an unserer Umfrage!',
  introText:
    'Wir bedanken uns für Ihre Anregungen und Hinweise. Durch die Auswertung Ihrer Antworten helfen Sie uns dabei, unsere internen Abläufe zu optimieren sowie Schwachstellen zu identifizieren, sodass wir uns als Unternehmen verbessern können.',
  outroText: 'Ihr Team von [Firmenname]',
  scale: {
    highText: 'Sehr wahrscheinlich',
    lowText: 'Sehr unwahrscheinlich',
  },
  feedbackActive: false,
  feedbackText: 'Feedback / konstruktive Kritik / Verbesserungsvorschläge',
  feedbackTitle: 'Was hat Sie zu Ihrer Bewertung veranlasst? (optional)',
};

interface Props {
  id?: string;
  loadSurveyType?: string;
  isEdit?: boolean;
  reload: () => Promise<void>;
}

const NetPromoterScoreGuide: FC<Props> = ({ id, isEdit, reload, loadSurveyType }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRole } = useContext(AuthContext);

  const [npsScoreSlider, setNpsScoreSlider] = useState<number>(defaultNpsData.score);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentSurveyGuideStep, setCurrentSurveyGuideStep] = useState(0);
  const [canEditDesign, setCanEditDesign] = useState(false);
  const [showContactData, setShowContactData] = useState(defaultNpsData.contactForm);
  const [design, setDesign] = useState<IDesign>(defaultDesign);
  const [npsData, setNpsData] = useState<INpsEntry>(defaultNpsData);
  const [showUrlError, setShowUrlError] = useState<boolean>(false);
  const [contactData, setContactData] = useState<IContactData>(defaultContactData);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(npsData.feedbackActive);
  const [surveyID, setSurveyID] = useState<string>(id || '');

  const handleNextSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep + 1);
  };

  const handlePreviousSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep - 1);
  };

  const isValidPlatformUrl = (url: string) => {
    return url.startsWith('http://') || url.startsWith('https://');
  };

  const isValidCompanyUrl = (url: string): boolean => {
    const validEndings = ['.com', '.de', '.org', '.net', '.info', '.at', '.eu', '.ch'];
    const pattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
    return pattern.test(url) && validEndings.some((ending) => url.endsWith(ending));
  };

  const handleAddSurvey = async () => {
    const getMergedState = () => ({
      ...npsData,
      ...contactData,
      ...design,
    });
    const mergedState: ISurvey = getMergedState();
    if (isEdit) {
      if (!id) return;
      await SurveyService.updateNpsSurvey(id, mergedState);
    } else {
      mergedState.slug = '';
      await SurveyService.addNpsSurvey(mergedState);
    }
    await reload();
  };

  const handleOutroTextChange = (companyName: string) => {
    const value = npsData.outroText.replace(/\[Firmenname\]/, companyName || '[Firmenname]');
    setNpsData((prevState) => ({
      ...prevState,
      outroText: value,
    }));
  };

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async (signal: AbortSignal) => {
      const platforms = await PlatformService.getPlatforms(setLoading, signal);
      if (!platforms) return;
      setPlatforms(platforms);
    };

    (async () => await getPlatforms(controller.signal))();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!hasRole('can-use-survey')) {
      navigate('/');
    }
  }, [hasRole, navigate]);

  useEffect(() => {
    if (!surveyID) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const getSurvey = async () => {
      setLoading(true);
      try {
        const endpointPath = SurveyUtils.getEndpointByType(loadSurveyType);
        const survey = await SurveyService.getSurveyPublic(
          surveyID,
          endpointPath,
          setLoading,
          signal
        );
        if (!survey) {
          setLoading(false);
          return;
        }
        if (survey.type === 'NPS') {
          setNpsData(survey);
        }
        setDesign({
          companyLogo: survey.companyLogo,
          primaryColor: survey.primaryColor,
          secondaryColor: survey.secondaryColor,
          customFont: survey.customFont,
          customFontFormat: survey.customFontFormat,
        });
        setContactData({
          privacyPolicyLink: survey.privacyPolicyLink,
          contactEmail: survey.contactEmail,
          imprintLink: survey.imprintLink,
        });
        setIsCheckboxChecked(survey.feedbackActive);
        setShowContactData(
          survey.imprintLink !== '' || survey.contactEmail !== '' || survey.privacyPolicyLink !== ''
        );
        setCanEditDesign(
          survey.primaryColor !== '#357E8B' ||
            survey.secondaryColor !== '#000000' ||
            survey.companyLogo !== ''
        );
      } catch (error) {
        console.error('Failed to fetch survey:', error);
      } finally {
        setLoading(false);
      }
    };

    getSurvey();

    return () => {
      controller.abort();
    };
  }, [surveyID]);

  return (
    <>
      {!isEdit ? (
        <>
          <Box sx={{ maxWidth: '600px' }}>
            <Stepper
              activeStep={currentSurveyGuideStep}
              orientation="vertical"
            >
              <Step>
                <StepLabel>{t('survey_label')}</StepLabel>
                <StepContent>
                  <TextField
                    label={t('survey_name')}
                    placeholder="z.B.: Musterfirma"
                    variant="outlined"
                    size="small"
                    value={npsData.label}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        label: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: 400 }}
                    autoFocus
                  />
                  <Box sx={{ mb: theme.spacing(1) }}>
                    <Button
                      disabled={npsData.label === ''}
                      variant="contained"
                      onClick={handleNextSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{'Frage & Score'}</StepLabel>
                <StepContent>
                  <TextField
                    label={t('nps_survey')}
                    size={'small'}
                    multiline
                    maxRows={'2'}
                    variant={'outlined'}
                    value={npsData.question}
                    placeholder={t('nps_survey')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        question: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
                    <Typography>{'NPS-Score'}</Typography>
                    <Slider
                      marks
                      valueLabelDisplay={'auto'}
                      value={npsData.score ? npsData.score : npsScoreSlider}
                      min={0}
                      max={10}
                      size={'small'}
                      onChange={(event: Event, value: number | number[]) => {
                        setNpsScoreSlider(value as number);
                        setNpsData((prevState) => ({
                          ...prevState,
                          score: value as number,
                        }));
                      }}
                    />
                  </Box>
                  <Typography>
                    Kommentarfeld aktivieren{' '}
                    <Checkbox
                      checked={isCheckboxChecked}
                      onChange={() => {
                        setIsCheckboxChecked((prevState) => !prevState);
                        setNpsData((prevState) => ({
                          ...prevState,
                          feedbackActive: !prevState.feedbackActive,
                        }));
                      }}
                    />
                  </Typography>
                  <Box sx={{ mb: theme.spacing(2), mt: theme.spacing(1) }}>
                    <Button
                      disabled={npsData.question === ''}
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              {isCheckboxChecked && (
                <Step>
                  <StepLabel>{`${t('comment')} (${t('optional_configuration')})`} </StepLabel>
                  <StepContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                      <TextField
                        label={'Kommenarfeld-Titel'}
                        size={'small'}
                        multiline
                        maxRows={'2'}
                        variant={'outlined'}
                        value={npsData.feedbackTitle}
                        placeholder={'Was hat Sie zu Ihrer Bewertung veranlasst? (optional)'}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setNpsData((prevState) => ({
                            ...prevState,
                            feedbackTitle: event.target.value,
                          }));
                        }}
                        sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                      />
                      <TextField
                        label={'Kommentarfeld'}
                        size={'medium'}
                        multiline
                        maxRows={'3'}
                        variant={'outlined'}
                        value={npsData.feedbackText}
                        placeholder={
                          'Inhalt im Kommentarfeld: Feedback, konstruktive Kritik, Verbesserungsvorschläge'
                        }
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setNpsData((prevState) => ({
                            ...prevState,
                            feedbackText: event.target.value,
                          }));
                        }}
                        sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                      />
                    </Box>
                    <Box>
                      <Button
                        variant="contained"
                        onClick={() => {
                          handleNextSurveyGuideStep();
                        }}
                        sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                      >
                        {t('next')}
                      </Button>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handlePreviousSurveyGuideStep}
                        sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                      >
                        {t('cancel')}
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              )}
              <Step>
                <StepLabel>{t('scale_label')}</StepLabel>
                <StepContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                    <TextField
                      label={t('scale0_label')}
                      size="small"
                      variant="outlined"
                      placeholder={'z.B.: Sehr unwahrscheinlich'}
                      value={npsData.scale.lowText}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setNpsData((prevState) => ({
                          ...prevState,
                          scale: {
                            ...prevState.scale,
                            lowText: event.target.value,
                          },
                        }));
                      }}
                      fullWidth
                      sx={{ mt: theme.spacing(2) }}
                    />
                    <TextField
                      label={t('scale10_label')}
                      size="small"
                      variant="outlined"
                      placeholder={'z.B.: Sehr wahrscheinlich'}
                      value={npsData.scale.highText}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setNpsData((prevState) => ({
                          ...prevState,
                          scale: {
                            ...prevState.scale,
                            highText: event.target.value,
                          },
                        }));
                      }}
                      fullWidth
                      sx={{ mt: theme.spacing(2) }}
                    />
                  </Box>
                  <Box sx={{ mb: theme.spacing(1) }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      disabled={false}
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{t('company_data_label')}</StepLabel>
                <StepContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                    <TextField
                      label={t('company_name')}
                      size="small"
                      variant="outlined"
                      placeholder={'z.B.: Musterfirma GmbH'}
                      value={npsData.companyName}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setNpsData((prevState) => ({
                          ...prevState,
                          companyName: event.target.value,
                        }));
                      }}
                      fullWidth
                      sx={{ mt: theme.spacing(2) }}
                    />
                    <TextField
                      label={t('enter_company_url')}
                      size="small"
                      variant="outlined"
                      placeholder={'z.B.: example.com'}
                      value={npsData.companyWebsiteUrl}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setNpsData((prevState) => ({
                          ...prevState,
                          companyWebsiteUrl: event.target.value,
                        }));
                        setShowUrlError(false);
                      }}
                      error={showUrlError}
                      helperText={showUrlError ? t('url_invalid') : ''}
                      sx={{ mt: theme.spacing(2), color: showUrlError ? 'red' : 'inherit' }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ mb: theme.spacing(2) }}>
                    <Button
                      disabled={!npsData.companyName || !npsData.companyWebsiteUrl || showUrlError}
                      variant="contained"
                      onClick={() => {
                        if (!isValidCompanyUrl(npsData.companyWebsiteUrl)) {
                          setShowUrlError(true);
                          return;
                        }
                        handleNextSurveyGuideStep();
                        handleOutroTextChange(npsData.companyName);
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      disabled={false}
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{'Titel'}</StepLabel>
                <StepContent>
                  <TextField
                    label={'Titel'}
                    size={'small'}
                    multiline
                    maxRows={'3'}
                    variant={'outlined'}
                    value={npsData.title}
                    placeholder={'Bitte teilen Sie uns Ihre aktuelle Stimmung mit.'}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        title: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                  />
                  <Box sx={{ mb: theme.spacing(2) }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{'Intro & Outro'}</StepLabel>
                <StepContent>
                  <TextField
                    label={'Intro'}
                    size={'small'}
                    multiline
                    maxRows={'6'}
                    variant={'outlined'}
                    value={npsData.introText}
                    placeholder={`${npsData.introText} ${npsData.companyName}`}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        introText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                  />
                  <TextField
                    label={'Outro'}
                    size={'small'}
                    multiline
                    maxRows={'3'}
                    variant={'outlined'}
                    value={npsData.outroText}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        outroText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                  />
                  <Box sx={{ mb: theme.spacing(2) }}>
                    <Button
                      //disabled={NpsData.question === ''}
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{`${t('portal_data')} (${t('optional_configuration')})`}</StepLabel>
                <StepContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                    <PlatformSelect
                      value={npsData.platform}
                      onChange={(event: SelectChangeEvent) => {
                        setNpsData((prevState) => ({
                          ...prevState,
                          platform: event.target.value,
                        }));
                      }}
                      platforms={platforms}
                    />
                    <TextField
                      label={`${t('portal_data')}-URL`}
                      size="small"
                      variant="outlined"
                      placeholder={'z.B.: https://www.kununu.de/bewertungen'}
                      value={npsData.platformUrl}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setNpsData((prevState) => ({
                          ...prevState,
                          platformUrl: event.target.value,
                        }));
                        setShowUrlError(false);
                      }}
                      error={showUrlError}
                      helperText={showUrlError ? t('url_invalid') : ''}
                      sx={{ mt: theme.spacing(2), color: showUrlError ? 'red' : 'inherit' }}
                      fullWidth
                    />
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (
                          !isValidPlatformUrl(npsData.platformUrl) &&
                          npsData.platformUrl !== ''
                        ) {
                          setShowUrlError(true);
                          return;
                        }
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      disabled={false}
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>{t('finish_configuration')}</StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<Check />}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                      onClick={handleAddSurvey}
                    >
                      {t('finish')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={npsData.lang}
                    exclusive
                    onChange={(event: MouseEvent, value: string | null) => {
                      if (value) {
                        setNpsData((prevState) => ({
                          ...prevState,
                          lang: value,
                        }));
                      }
                    }}
                    sx={{
                      '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ToggleButton value="de">
                      <FlagDe
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="en">
                      <FlagGb
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="nl">
                      <FlagNl
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Design</Typography>}
                />

                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                onChange={setContactData}
              />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ maxWidth: '600px' }}>
            <Stepper
              activeStep={currentSurveyGuideStep}
              orientation="vertical"
            >
              <Step>
                <StepLabel>{t('survey_label')}</StepLabel>
                <StepContent>
                  <TextField
                    label={t('survey_name')}
                    placeholder={'z.B.: Musterfirma'}
                    size={'small'}
                    variant="outlined"
                    sx={{ minWidth: '400px' }}
                    autoFocus
                    value={npsData.label}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        label: e.target.value,
                      }));
                    }}
                  />
                  <Box sx={{ mb: theme.spacing(1) }}>
                    <Button
                      disabled={!npsData.label}
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              <Step>
                <StepLabel>Kommentarfeld</StepLabel>
                <StepContent>
                  <Typography>
                    Kommentarfeld aktivieren{' '}
                    <Checkbox
                      checked={isCheckboxChecked}
                      onChange={() => {
                        setIsCheckboxChecked((prevState) => !prevState);
                        setNpsData((prevState) => ({
                          ...prevState,
                          feedbackActive: !prevState.feedbackActive,
                        }));
                      }}
                    />
                  </Typography>
                  <Box sx={{ mb: theme.spacing(1) }}>
                    <Button
                      disabled={npsScoreSlider === 0}
                      variant="contained"
                      onClick={() => {
                        handleNextSurveyGuideStep();
                      }}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('next')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
              {isCheckboxChecked && (
                <Step>
                  <StepLabel>{`${t('comment')} (${t('optional_configuration')})`} </StepLabel>
                  <StepContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                      <TextField
                        label={'Kommenarfeld-Titel'}
                        size={'small'}
                        multiline
                        maxRows={'2'}
                        variant={'outlined'}
                        value={npsData.feedbackTitle}
                        placeholder={'Was hat Sie zu Ihrer Bewertung veranlasst? (optional)'}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setNpsData((prevState) => ({
                            ...prevState,
                            feedbackTitle: event.target.value,
                          }));
                        }}
                        sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                      />
                      <TextField
                        label={'Kommentarfeld'}
                        size={'medium'}
                        multiline
                        maxRows={'3'}
                        variant={'outlined'}
                        value={npsData.feedbackText}
                        placeholder={
                          'Inhalt im Kommentarfeld: Feedback, konstruktive Kritik, Verbesserungsvorschläge'
                        }
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setNpsData((prevState) => ({
                            ...prevState,
                            feedbackText: event.target.value,
                          }));
                        }}
                        sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                      />
                    </Box>
                    <Box>
                      <Button
                        variant="contained"
                        onClick={() => {
                          handleNextSurveyGuideStep();
                        }}
                        sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                      >
                        {t('next')}
                      </Button>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handlePreviousSurveyGuideStep}
                        sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                      >
                        {t('cancel')}
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              )}
              <Step>
                <StepLabel>{t('finish_configuration')}</StepLabel>
                <StepContent>
                  <Box sx={{ mb: theme.spacing(2) }}>
                    <Button
                      variant="contained"
                      startIcon={<Check />}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                      onClick={handleAddSurvey}
                    >
                      {t('finish')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handlePreviousSurveyGuideStep}
                      sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={npsData.lang}
                    exclusive
                    onChange={(event: MouseEvent<HTMLElement>, value: string | null) => {
                      if (value) {
                        setNpsData((prevState) => ({
                          ...prevState,
                          lang: value,
                        }));
                      }
                    }}
                    sx={{
                      '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ToggleButton value="de">
                      <FlagDe
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="en">
                      <FlagGb
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="nl">
                      <FlagNl
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Design</Typography>}
                />

                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                onChange={setContactData}
              />
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default NetPromoterScoreGuide;
